import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Params } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { mapDocToCopyrightData } from 'app/logon/login-form/copyright/utils/map-login-doc-to-copyright-data';
import { mapLoginDocToDocumentsData } from 'app/logon/login-form/documents/utils/map-login-doc-to-documents-data';
import { mapLoginDocToRegisterData } from 'app/logon/login-form/register/utils/map-login-doc-to-register-data';
import { mapLoginDocToSocialData } from 'app/logon/login-form/social/utils/map-login-doc-to-social-data';
import { mapLoginDocToFormDoc } from 'app/logon/login-form/utils/map-login-doc-to-form-doc';
import {
  IAuthDocument,
  ILogonDocument,
} from 'app/services/api5-service/api.interface';
import { HttpBaseService } from 'app/services/http-base-service/http-base.service';
import { PrefetchService } from 'app/services/prefetch-service/prefetch.service';
import { IFormButtonUi } from 'components/form-button/model';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { IFormModel } from 'common/dynamic-form/dynamic-form.interface';
import { GlobalPreloaderService } from 'common/global-preloader/global-preloader.service';
import { convertParamsToStringWithEncode } from 'utils/convert-params-to-string';
import { filterDefined } from 'utils/is-defined';

@UntilDestroy()
@Injectable()
export class AppService {
  private readonly docSubject: BehaviorSubject<ILogonDocument | null> =
    new BehaviorSubject(null);

  get doc(): ILogonDocument {
    if (!this.docSubject.value) {
      throw new Error('doc is null!');
    }
    return this.docSubject.value;
  }

  readonly loginDoc$ = this.docSubject.pipe(
    filterDefined(),
    tap(doc => {
      const title = doc.messages?.msg?.title;
      if (title) {
        this.titleService.setTitle(title);
      }
    }),
    map(doc => mapLoginDocToFormDoc(doc)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  readonly socialData$ = this.loginDoc$.pipe(
    map(doc => mapLoginDocToSocialData(doc)),
  );

  readonly registerData$ = this.loginDoc$.pipe(
    map(doc => mapLoginDocToRegisterData(doc)),
  );

  readonly documentsData$ = this.loginDoc$.pipe(
    map(doc => mapLoginDocToDocumentsData(doc)),
  );

  readonly copyrightData$ = this.loginDoc$.pipe(
    map(doc => mapDocToCopyrightData(doc)),
  );

  constructor(
    private readonly httpBaseService: HttpBaseService,
    private readonly prefetchService: PrefetchService,
    private readonly globalPreloaderService: GlobalPreloaderService,
    private readonly titleService: Title,
  ) {
    this.globalPreloaderService.preloadElementId = 'isp-global-preloader';
  }

  authHandler(model: IFormModel, button: IFormButtonUi): void {
    const request = this.httpBaseService.post<IAuthDocument & ILogonDocument>({
      ...model,
      forget: 'on',
      func: 'auth',
    });

    button.preloaderSubject.next(true);
    request.pipe(untilDestroyed(this)).subscribe(doc => {
      // @WARN it's copy logic redirect on success auth from xsl
      if (doc.auth && !doc.noproject) {
        const queryParams = [
          doc.tparams.sfrom?.$ === 'sessionexpired'
            ? 'sfrom=loginform'
            : undefined,
          doc.auth.$redirect,
        ]
          .filter(Boolean)
          .join('&');
        location.href = `${doc.$binary}${queryParams ? '?' : ''}${queryParams}`;
        return;
      }

      button.preloaderSubject.next(false);
      this.docSubject.next(doc);
    });
  }

  passwordRecoveryHandler(button: IFormButtonUi): void {
    const params: Params = {
      func: button.func,
    };

    const doc = this.doc;

    if (doc.loginform.redirect) {
      const redirect = doc.loginform.redirect.$;
      params.redirect = redirect;
    }
    // @TODO d.bronnikov remove redirect after refactoring project to SPA, open extform by routing
    const queryParamsString = convertParamsToStringWithEncode(params);
    location.href = `${doc.$host}${doc.$binary}?${queryParamsString}`;
  }

  changeLanguageHandler$(model: IFormModel): Observable<ILogonDocument> {
    const request = this.httpBaseService.post<ILogonDocument>({
      username: model.username,
      pwd: model.password,
      lang: model.lang,
      func: 'logon',
    });
    return request.pipe(tap(doc => this.docSubject.next(doc)));
  }

  fetchInitDoc(): void {
    this.prefetchService
      .getPrefetch$<ILogonDocument>()
      .pipe(untilDestroyed(this))
      .subscribe(doc => {
        this.docSubject.next(doc);
        this.globalPreloaderService.turnOff();
      });
  }
}
